import '../upsert-collection-layer/upsert-collection-layer.js';
import '../wishlist-collection/wishlist-collection.js';
import { CSSResult, html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Messages, SharedCollection } from '../../../@types/wishlist.js';
import { repeat } from 'lit/directives/repeat.js';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import styles from './wishlist-curated-collections.scss?inline';
import { consume } from '@lit/context';
import {
  collectionsContext,
  curatedCollectionContext,
  productsContext,
} from '../../../scripts/context/wishlistContext.js';
import { CollectionsState, CuratedCollectionsState, ProductsState } from '../../../scripts/context/wishlist-state.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import {
  TrackedCuratedCollection,
  trackPageView,
  trackViewCollections,
  trackViewCuratedCollections,
  trackViewWishlist,
} from '../../../scripts/service/cube-tracking-layer.js';
import { curatorLayerContext, CuratorLayerState } from '../../../scripts/context/curatorLayerContext.js';
import { WishlistService } from '../../../scripts/service/wishlist-service.js';
import { vtoContext } from '../../../scripts/context/vtoContext.js';
import { VtoService } from '../../../scripts/service/vto-service.js';
import { VtoTeaserController } from '../vto-curated-teaser/vto-teaser-controller.js';

@customElement('ents-wishlist-curated-collections')
export class WishlistCuratedCollections extends LitElement {
  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({ context: curatedCollectionContext })
  private curatedCollectionsState!: CuratedCollectionsState;

  @consume({ context: collectionsContext })
  private collectionsState!: CollectionsState;

  @consume({ context: productsContext })
  private productsState!: ProductsState;

  @consume({ context: curatorLayerContext, subscribe: true })
  @property({ attribute: false })
  private curatorLayerState!: CuratorLayerState;

  @consume({ context: i18nContext })
  private messages!: Messages;

  @consume({ context: vtoContext })
  private vtoService?: VtoService;

  @state()
  private collections: SharedCollection[] = [];

  @property({ type: Boolean, attribute: 'logged-in' }) private loggedIn = false;

  private readonly vtoTeaser = new VtoTeaserController(this, {
    isEnabled: () => true,
    getAppConfig: () => this.wishlistAppConfig,
    getVtoService: () => this.vtoService,
    getMessages: () => this.messages,
    isLoggedIn: () => this.loggedIn,
    collectItems: () =>
      this.collections.flatMap((col) =>
        col.products.flatMap((p) => (p.colorId ? [{ syan: p.syan, colorId: p.colorId }] : [])),
      ),
    showStatusSnackbars: true,
  });

  private trackingSent = false;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  async loadCollections() {
    try {
      this.collections = await this.curatedCollectionsState.getAll(this.wishlistAppConfig.curatedCollectionApiEndpoint);
    } catch {
      this.collections = [];
    }
    this.trackPageEvents();
  }

  override connectedCallback(): void {
    super.connectedCallback();
    void this.loadCollections();
  }

  private trackPageEvents() {
    if (this.trackingSent) {
      return;
    }
    this.trackingSent = true;
    trackPageView('curated_collection_list', this.collections.length === 0, true);
    trackViewWishlist('curated_collection_list', this.productsState.entities);
    trackViewCollections('curated_collection_list', this.collectionsState.entities, this.productsState);

    const allCuratedCollections = this.curatedCollectionsState.entities;
    const curatedCollectionsToTrack: TrackedCuratedCollection[] = allCuratedCollections.map((entry) => ({
      collection: entry.collection,
      quantity: entry.products.length,
    }));
    trackViewCuratedCollections('curated_collection_list', curatedCollectionsToTrack);
  }

  private openCuratorDrawer() {
    const curator = this.collections[0]?.curator;
    if (curator) {
      this.curatorLayerState.update({ open: true, curator });
    }
  }

  private renderCurator() {
    const curator = this.collections[0]?.curator;

    if (!curator) return nothing;

    const subtitle = this.messages['ents_wishlist_curated_collections_curator_subtitle'] ?? '';
    const subtitleParts = subtitle.split('{}');

    return html`
      <div class="ents-wishlist-curator">
        <img src="${curator.image}" alt="${curator.name}" class="ents-wishlist-curator__image" />
        <h2 class="ents-heading">${this.messages['ents_wishlist_curated_collections_curator_title']}</h2>

        <p>${subtitleParts[0]}<strong>${curator.name}</strong>${subtitleParts[1] || ''}</p>

        <button
          class="bdk-button"
          type="button"
          variant="outline"
          size="small"
          @click="${this.openCuratorDrawer}"
          data-test-ents-curator-details-button
        >
          ${this.messages['ents_wishlist_curated_collection_hint_linktext']}
        </button>
      </div>
    `;
  }

  render() {
    return html`
      <div class="ents-wishlist-collection-container">
        <h2 class="ents-heading-medium">${this.messages['ents_wishlist_curated_collections_title']}</h2>
        <p>${this.messages['ents_wishlist_curated_collections_subtitle']}</p>
        ${this.vtoTeaser.renderTop()}
        <div class="ents-wishlist-collection-grid">
          ${repeat(
            this.collections,
            (col) => col.collection.id,
            (col) => html`
              <article>
                <ents-wishlist-collection
                  .wishlistCollection="${col.collection}"
                  .products="${WishlistService.getProductsForCollectionWithDefaultSorting(
                    col.products,
                    col.collection.id,
                  )}"
                  curated-collection
                />
              </article>
            `,
          )}
        </div>
        ${this.renderCurator()} ${this.vtoTeaser.renderBottom()}
      </div>
    `;
  }
}
